import { type EventChannel, eventChannel, type Task } from 'redux-saga';
import { call, cancel, delay, fork, put, take, takeEvery } from 'redux-saga/effects';

import { type GeneratorResult } from '@pressreader/appstore';
import { userChangeDone, UserChangeReason } from '@pressreader/authentication-core';
import { collectionChangeSubscription } from '@pressreader/social-api';

import { resetUserCollectionsAction } from '../actions';

/**
 * Compensation for server-side eventual consistency of collections metadata after collection changes (create/update/delete).
 */
const COLLECTION_CHANGE_DEBOUNCE_MS = 100;

function createCollectionChangeChannel(): EventChannel<true> {
    return eventChannel<true>(emit => {
        const sub = collectionChangeSubscription.subscribe(() => {
            emit(true);
        });
        return () => {
            sub.unsubscribe();
        };
    });
}

function* deferredCollectionsUpdateSaga(): GeneratorResult<void> {
    yield delay(COLLECTION_CHANGE_DEBOUNCE_MS);
    yield put(resetUserCollectionsAction());
}

function* watchCollectionChanges(): GeneratorResult<void> {
    let deferredUpdateTask: Task | null = null;
    const channel: ReturnType<typeof createCollectionChangeChannel> = yield call(createCollectionChangeChannel);

    try {
        while (true) {
            yield take(channel);

            if (deferredUpdateTask) {
                yield cancel(deferredUpdateTask);
            }
            deferredUpdateTask = yield fork(deferredCollectionsUpdateSaga);
        }
    } finally {
        channel.close();
    }
}

function* onUserChangeDone(action: ReturnType<typeof userChangeDone>) {
    const { reason } = action.payload;
    if (reason === UserChangeReason.Logout) {
        yield put(resetUserCollectionsAction());
    }
}

function* saga() {
    yield fork(watchCollectionChanges);
    yield takeEvery(userChangeDone, onUserChangeDone);
}

export { saga };
